import {
  type ReactNode,
  useMemo,
  useState,
} from 'react';

import {
  isAxiosError,
} from 'axios';

import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  executePricingDecision,
  preflightPricingDecision,
} from '../services/pricingDecisionService';

import PricingDecisionContextEvidencePanel
  from './PricingDecisionContextEvidencePanel';

import type {
  PricingDecisionContextEvidence,
  PricingDecisionPreflightCustomer,
  PricingDecisionPreflightProduct,
  PricingDecisionPreflightRequest,
  PricingDecisionPreflightSource,
  PricingDecisionStrategyProfile,
} from '../types/pricingDecision';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


interface Props {
  source: PricingDecisionPreflightSource;

  customer: PricingDecisionPreflightCustomer;
  product: PricingDecisionPreflightProduct;

  currencyCode: string | null;

  referencePrice?: number | null;

  contextLabel: string;
  contextDetail: string;

  contextEvidence?:
    PricingDecisionContextEvidence
    | null;

  contextSupplement?:
    ReactNode;
}


const STRATEGY_PRESETS: Array<{
  value: Exclude<
    PricingDecisionStrategyProfile,
    'manual'
  >;
  label: string;
  sensitivity: number;
  description: string;
}> = [
  {
    value: 'conservador',
    label: 'Conservadora',
    sensitivity: 25,
    description:
      'Mayor peso de la inercia comercial.',
  },
  {
    value: 'equilibrado',
    label: 'Equilibrada',
    sensitivity: 50,
    description:
      'Balance entre inercia y señal del modelo.',
  },
  {
    value: 'agresivo',
    label: 'Agresiva',
    sensitivity: 90,
    description:
      'Mayor peso del escenario IA.',
  },
];


const numberFormatter =
  pricingNumberFormat({
      maximumFractionDigits: 2,
    },
  );


function formatNumber(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return numberFormatter.format(
    value,
  );
}


function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  if (!currencyCode) {
    return formatNumber(value);
  }

  try {
    return pricingNumberFormat({
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      },
    ).format(value);
  } catch {
    return formatNumber(value);
  }
}


function formatPct(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    numberFormatter.format(value)
  } %`;
}


function formatSignedPct(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${
    numberFormatter.format(value)
  } %`;
}


function formatDate(
  value: string | null | undefined,
): string {
  if (!value) {
    return '—';
  }

  const normalized =
    value.slice(0, 10);

  const [
    year,
    month,
    day,
  ] =
    normalized
      .split('-')
      .map(Number);

  if (
    !year
    || !month
    || !day
  ) {
    return normalized;
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  ).format(
    new Date(
      year,
      month - 1,
      day,
    ),
  );
}


function parsePositiveNumber(
  value: string,
): number | null {
  const normalized =
    value.trim();

  if (!normalized) {
    return null;
  }

  const parsed =
    Number(
      normalized.replace(
        ',',
        '.',
      ),
    );

  if (
    !Number.isFinite(parsed)
    || parsed <= 0
  ) {
    return null;
  }

  return parsed;
}


function calculateRelativeDeltaPct(
  referenceValue:
    number | null | undefined,
  candidateValue:
    number | null | undefined,
): number | null {
  if (
    referenceValue === null
    || referenceValue === undefined
    || candidateValue === null
    || candidateValue === undefined
    || referenceValue === 0
  ) {
    return null;
  }

  return (
    (
      candidateValue
      - referenceValue
    )
    / referenceValue
  ) * 100;
}


function getStrategyLabel(
  profile:
    PricingDecisionStrategyProfile
    | string
    | null
    | undefined,
): string {
  switch (profile) {
    case 'conservador':
      return 'Conservadora';

    case 'equilibrado':
      return 'Equilibrada';

    case 'agresivo':
      return 'Agresiva';

    case 'manual':
      return 'Manual';

    default:
      return '—';
  }
}


function getReviewModeLabel(
  value: string | null | undefined,
): string {
  switch (value) {
    case 'evaluation_possible':
      return 'Evaluación posible';

    case 'review_required':
      return 'Revisión requerida';

    case 'reference_only':
      return 'Solo referencia';

    default:
      return '—';
  }
}


function getRiskLabel(
  value: string | null | undefined,
): string {
  switch (value) {
    case 'low':
      return 'Bajo';

    case 'medium':
      return 'Medio';

    case 'high':
      return 'Alto';

    default:
      return '—';
  }
}


function getExecutionErrorMessage(
  error: unknown,
): string {
  if (!isAxiosError(error)) {
    return (
      error instanceof Error
        ? error.message
        : 'Error no identificado.'
    );
  }

  const data =
    error.response?.data as
      | {
          detail?: unknown;
        }
      | undefined;

  const detail =
    data?.detail;

  if (
    typeof detail === 'string'
  ) {
    return detail;
  }

  if (
    detail
    && typeof detail === 'object'
  ) {
    const record =
      detail as Record<
        string,
        unknown
      >;

    const parts: string[] = [];

    if (
      typeof record.code
      === 'string'
    ) {
      parts.push(
        record.code,
      );
    }

    if (
      typeof record.status
      === 'string'
    ) {
      parts.push(
        record.status,
      );
    }

    if (
      Array.isArray(
        record.reason_codes,
      )
    ) {
      parts.push(
        ...record
          .reason_codes
          .filter(
            (
              value,
            ): value is string =>
              typeof value
              === 'string',
          ),
      );
    }

    if (
      parts.length > 0
    ) {
      return parts.join(
        ' · ',
      );
    }
  }

  return (
    `HTTP ${
      error.response?.status
      ?? 'desconocido'
    }`
  );
}


function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <Typography
        variant="body2"
        sx={{
          color: '#94a3b8',
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: '#f8fafc',
          fontWeight: 750,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}


function DecisionScenarioCard({
  label,
  price,
  marginPct,
  markupPct,
  cost,
  referencePrice,
  currencyCode,
}: {
  label: string;

  price:
    number | null | undefined;

  marginPct:
    number | null | undefined;

  markupPct:
    number | null | undefined;

  cost:
    number | null | undefined;

  referencePrice:
    number | null | undefined;

  currencyCode: string | null;
}) {
  const delta =
    calculateRelativeDeltaPct(
      referencePrice,
      price,
    );

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: 1.5,
        borderRadius: 2,

        border:
          '1px solid rgba(148,163,184,0.14)',

        bgcolor:
          'rgba(15,23,42,0.34)',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: '#94a3b8',
          fontWeight: 850,
          letterSpacing: 0.75,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h5"
        sx={{
          mt: 0.15,
          color: '#f8fafc',
          fontWeight: 900,
          letterSpacing: '-0.02em',
        }}
      >
        {formatCurrency(
          price,
          currencyCode,
        )}
      </Typography>

      <Stack
        spacing={0.65}
        sx={{
          mt: 1.35,
        }}
      >
        <DetailRow
          label="Margen s/ingresos"
          value={
            formatPct(
              marginPct,
            )
          }
        />

        <DetailRow
          label="Markup s/coste"
          value={
            formatPct(
              markupPct,
            )
          }
        />

        <DetailRow
          label="Coste optimizador"
          value={
            formatCurrency(
              cost,
              currencyCode,
            )
          }
        />

        {referencePrice !== null
          && referencePrice !== undefined && (
          <DetailRow
            label="Δ vs precio observado"
            value={
              formatSignedPct(
                delta,
              )
            }
          />
        )}
      </Stack>
    </Paper>
  );
}


function RecommendationDecisionCard({
  price,
  marginPct,
  markupPct,
  cost,
  referencePrice,
  haircutVsMaxAiPct,
  currencyCode,
}: {
  price:
    number | null | undefined;

  marginPct:
    number | null | undefined;

  markupPct:
    number | null | undefined;

  cost:
    number | null | undefined;

  referencePrice:
    number | null | undefined;

  haircutVsMaxAiPct:
    number | null | undefined;

  currencyCode: string | null;
}) {
  const delta =
    calculateRelativeDeltaPct(
      referencePrice,
      price,
    );

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,

        p: {
          xs: 1.5,
          md: 1.75,
        },

        borderRadius: 2,

        border:
          '1px solid rgba(56,189,248,0.38)',

        background:
          'linear-gradient(135deg, rgba(14,165,233,0.11) 0%, rgba(15,23,42,0.48) 100%)',

        boxShadow:
          '0 12px 30px rgba(2,6,23,0.18)',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: '#7dd3fc',
          fontWeight: 900,
          letterSpacing: 0.8,
        }}
      >
        Recomendación
      </Typography>

      <Box
        sx={{
          mt: 0.65,

          display: 'grid',

          gridTemplateColumns: {
            xs: '1fr',
            sm:
              'repeat(2, minmax(0, 1fr))',
          },

          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontWeight: 750,
            }}
          >
            Precio recomendado
          </Typography>

          <Typography
            sx={{
              mt: 0.1,
              color: '#f8fafc',

              fontSize: {
                xs: 28,
                md: 34,
              },

              lineHeight: 1.1,
              fontWeight: 950,
              letterSpacing: '-0.03em',
            }}
          >
            {formatCurrency(
              price,
              currencyCode,
            )}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontWeight: 750,
            }}
          >
            Margen sobre ingresos recomendado
          </Typography>

          <Typography
            sx={{
              mt: 0.1,
              color: '#f8fafc',

              fontSize: {
                xs: 28,
                md: 34,
              },

              lineHeight: 1.1,
              fontWeight: 950,
              letterSpacing: '-0.03em',
            }}
          >
            {formatPct(
              marginPct,
            )}
          </Typography>
        </Box>
      </Box>

      <Divider
        sx={{
          my: 1.4,
        }}
      />

      <Box
        sx={{
          display: 'grid',

          gridTemplateColumns: {
            xs:
              'repeat(2, minmax(0, 1fr))',

            lg:
              'repeat(4, minmax(0, 1fr))',
          },

          gap: 1.2,
        }}
      >
        <DetailRow
          label="Coste utilizado"
          value={
            formatCurrency(
              cost,
              currencyCode,
            )
          }
        />

        <DetailRow
          label="Markup s/coste"
          value={
            formatPct(
              markupPct,
            )
          }
        />

        <DetailRow
          label={
            referencePrice !== null
            && referencePrice !== undefined
              ? 'Δ precio vs observado'
              : 'Referencia observada'
          }
          value={
            referencePrice !== null
            && referencePrice !== undefined
              ? formatSignedPct(
                delta,
              )
              : 'No aplicable'
          }
        />

        <DetailRow
          label="Recorte vs máximo IA"
          value={
            formatPct(
              haircutVsMaxAiPct,
            )
          }
        />
      </Box>
    </Paper>
  );
}


function DecisionConfidenceScale({
  value,
}: {
  value:
    string | null | undefined;
}) {
  const normalized =
    (
      value
      ?? ''
    )
      .trim()
      .toUpperCase();

  const position =
    normalized === 'BAJA'
    || normalized === 'LOW'
      ? 16.67
      : normalized === 'MEDIA'
        || normalized === 'MEDIUM'
        ? 50
        : normalized === 'ALTA'
          || normalized === 'HIGH'
          ? 83.33
          : null;

  const displayLabel =
    position === null
      ? 'No disponible'
      : position < 30
        ? 'BAJA'
        : position < 70
          ? 'MEDIA'
          : 'ALTA';

  return (
    <Box
      sx={{
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 800,
          }}
        >
          Confianza de la recomendación
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
          }}
        >
          {displayLabel}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          mt: 1.25,
          height: 6,
          borderRadius: 999,

          bgcolor:
            'rgba(148,163,184,0.18)',
        }}
      >
        {position !== null && (
          <Box
            sx={{
              position: 'absolute',
              left: `${position}%`,
              top: '50%',
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: '#38bdf8',

              border:
                '2px solid rgba(15,23,42,0.95)',

              transform:
                'translate(-50%, -50%)',
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          mt: 0.7,

          display: 'grid',

          gridTemplateColumns:
            'repeat(3, minmax(0, 1fr))',
        }}
      >
        {[
          'Baja',
          'Media',
          'Alta',
        ].map(
          (
            label,
            index,
          ) => (
            <Typography
              key={label}
              variant="caption"
              sx={{
                color: '#64748b',

                textAlign:
                  index === 0
                    ? 'left'
                    : index === 1
                      ? 'center'
                      : 'right',
              }}
            >
              {label}
            </Typography>
          ),
        )}
      </Box>
    </Box>
  );
}


function DecisionRiskScale({
  score,
  label,
}: {
  score:
    number | null | undefined;

  label:
    string | null | undefined;
}) {
  const boundedScore =
    score === null
    || score === undefined
      ? null
      : Math.min(
        1,
        Math.max(
          0,
          score,
        ),
      );

  return (
    <Box
      sx={{
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 800,
          }}
        >
          Riesgo operativo
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
          }}
        >
          {getRiskLabel(
            label,
          )}

          {boundedScore !== null
            ? ` · ${
              numberFormatter.format(
                boundedScore,
              )
            } / 1,00`
            : ''}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          mt: 1.25,
          height: 6,
          borderRadius: 999,

          bgcolor:
            'rgba(148,163,184,0.18)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '45%',
            top: -2,
            bottom: -2,
            width: '1px',

            bgcolor:
              'rgba(148,163,184,0.42)',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: '75%',
            top: -2,
            bottom: -2,
            width: '1px',

            bgcolor:
              'rgba(148,163,184,0.42)',
          }}
        />

        {boundedScore !== null && (
          <Box
            sx={{
              position: 'absolute',

              left:
                `${
                  boundedScore
                  * 100
                }%`,

              top: '50%',
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: '#38bdf8',

              border:
                '2px solid rgba(15,23,42,0.95)',

              transform:
                'translate(-50%, -50%)',
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          mt: 0.65,

          display: 'grid',

          gridTemplateColumns:
            '45% 30% 25%',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            textAlign: 'left',
          }}
        >
          Bajo
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            textAlign: 'center',
          }}
        >
          Medio
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            textAlign: 'right',
          }}
        >
          Alto
        </Typography>
      </Box>
    </Box>
  );
}


export default function PricingDecisionOptimizationPanel({
  source,
  customer,
  product,
  currencyCode,
  referencePrice = null,
  contextLabel,
  contextDetail,
  contextEvidence = null,
  contextSupplement = null,
}: Props) {
  const [
    strategyProfile,
    setStrategyProfile,
  ] =
    useState<PricingDecisionStrategyProfile>(
      'equilibrado',
    );

  const [
    strategySensitivity,
    setStrategySensitivity,
  ] =
    useState(50);

  const [
    quantityInput,
    setQuantityInput,
  ] =
    useState('');

  const [
    manualCostInput,
    setManualCostInput,
  ] =
    useState('');

  const quantity =
    parsePositiveNumber(
      quantityInput,
    );

  const manualCost =
    parsePositiveNumber(
      manualCostInput,
    );

  const effectiveProduct =
    useMemo<
      PricingDecisionPreflightProduct
    >(
      () => ({
        ...product,

        standard_cost:
          manualCost
          ?? product.standard_cost,
      }),
      [
        manualCost,
        product,
      ],
    );

  const request =
    useMemo<
      PricingDecisionPreflightRequest
    >(
      () => ({
        version: '1.0',
        source,
        customer,
        product:
          effectiveProduct,

        commercial_inputs: {
          quantity,

          generate_pricing_matrix:
            true,

          strategy_profile:
            strategyProfile,

          strategy_sensitivity:
            strategySensitivity,
        },
      }),
      [
        customer,
        effectiveProduct,
        quantity,
        source,
        strategyProfile,
        strategySensitivity,
      ],
    );

  const preflightQuery =
    useQuery({
      queryKey: [
        'pricing-decision',
        'optimization-panel-preflight',
        request,
      ],

      queryFn: () =>
        preflightPricingDecision(
          request,
        ),

      staleTime: 15_000,
    });

  const executionMutation =
    useMutation({
      mutationFn: () =>
        executePricingDecision(
          request,
        ),
    });

  const result =
    executionMutation.data
    ?? null;

  const missingCost =
    preflightQuery.data
      ?.readiness
      .missing_required
      .some(
        (item) =>
          item.field
          === 'product.standard_cost',
      )
    ?? false;

  const ready =
    preflightQuery.data
      ?.status
      === 'ready';

  const resetExecution =
    () => {
      executionMutation.reset();
    };

  const applyStrategyPreset =
    (
      profile: Exclude<
        PricingDecisionStrategyProfile,
        'manual'
      >,
      sensitivity: number,
    ) => {
      setStrategyProfile(
        profile,
      );

      setStrategySensitivity(
        sensitivity,
      );

      resetExecution();
    };

  const errorMessage =
    executionMutation.isError
      ? getExecutionErrorMessage(
        executionMutation.error,
      )
      : null;

  return (
    <Stack
      spacing={1.5}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,

          border:
            '1px solid rgba(56,189,248,0.20)',

          background:
            'linear-gradient(135deg, rgba(14,165,233,0.085) 0%, rgba(15,23,42,0.66) 42%, rgba(99,102,241,0.045) 100%)',

          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: {
              xs: 1.75,
              md: 2,
            },

            py: 1.4,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: '#94a3b8',
                  fontWeight: 800,
                  letterSpacing: 0.8,
                }}
              >
                Configuración de la decisión
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.15,
                  color: '#cbd5e1',
                  fontWeight: 750,
                }}
              >
                Define cuánto peso tendrá la señal del
                optimizador frente a la inercia comercial.
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
            >
              <Chip
                size="small"
                label={
                  `${
                    getStrategyLabel(
                      strategyProfile,
                    )
                  } · ${
                    strategySensitivity
                  }%`
                }
                variant="outlined"
                sx={{
                  color: '#bae6fd',

                  borderColor:
                    'rgba(56,189,248,0.28)',

                  bgcolor:
                    'rgba(14,165,233,0.06)',

                  fontWeight: 800,
                }}
              />

              <Chip
                size="small"
                label={
                  preflightQuery.isFetching
                    ? 'Comprobando contexto'
                    : ready
                      ? 'Preparado'
                      : preflightQuery
                          .data
                          ?.status
                        === 'needs_input'
                        ? 'Falta información'
                        : 'No disponible'
                }
                variant="outlined"
                sx={{
                  color:
                    ready
                      ? '#a7f3d0'
                      : '#cbd5e1',

                  borderColor:
                    ready
                      ? 'rgba(52,211,153,0.28)'
                      : 'rgba(148,163,184,0.20)',
                }}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              mt: 1.4,

              display: 'grid',

              gridTemplateColumns: {
                xs: '1fr',

                lg:
                  'minmax(0, 1.15fr) minmax(360px, 0.85fr)',
              },

              gap: {
                xs: 1.75,
                lg: 3,
              },

              alignItems: 'start',
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.7,
                  color: '#64748b',
                  fontWeight: 750,
                }}
              >
                Perfil estratégico
              </Typography>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={
                  strategyProfile
                  === 'manual'
                    ? null
                    : strategyProfile
                }
                onChange={(
                  _,
                  nextProfile:
                    Exclude<
                      PricingDecisionStrategyProfile,
                      'manual'
                    >
                    | null,
                ) => {
                  if (
                    !nextProfile
                  ) {
                    return;
                  }

                  const preset =
                    STRATEGY_PRESETS.find(
                      (item) =>
                        item.value
                        === nextProfile,
                    );

                  if (!preset) {
                    return;
                  }

                  applyStrategyPreset(
                    preset.value,
                    preset.sensitivity,
                  );
                }}
                disabled={
                  executionMutation
                    .isPending
                }
                sx={{
                  width: '100%',

                  '& .MuiToggleButton-root':
                    {
                      flex: 1,
                      minWidth: 0,
                      px: 1,
                      py: 0.9,

                      textTransform:
                        'none',

                      color: '#94a3b8',

                      borderColor:
                        'rgba(148,163,184,0.18)',

                      '&.Mui-selected':
                        {
                          color:
                            '#e0f2fe',

                          bgcolor:
                            'rgba(56,189,248,0.12)',
                        },

                      '&.Mui-selected:hover':
                        {
                          bgcolor:
                            'rgba(56,189,248,0.16)',
                        },
                    },
                }}
              >
                {STRATEGY_PRESETS.map(
                  (preset) => (
                    <ToggleButton
                      key={
                        preset.value
                      }
                      value={
                        preset.value
                      }
                    >
                      <Box
                        sx={{
                          minWidth: 0,
                          textAlign: 'left',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'inherit',
                            fontWeight: 850,
                          }}
                        >
                          {preset.label}
                          {' · '}
                          {preset.sensitivity}
                          %
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: {
                              xs: 'none',
                              md: 'block',
                            },

                            mt: 0.15,
                            color: '#64748b',
                            lineHeight: 1.35,

                            whiteSpace:
                              'normal',
                          }}
                        >
                          {preset.description}
                        </Typography>
                      </Box>
                    </ToggleButton>
                  ),
                )}
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                spacing={2}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontWeight: 750,
                  }}
                >
                  Intensidad estratégica
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#38bdf8',
                    fontWeight: 900,
                  }}
                >
                  {strategySensitivity} %
                </Typography>
              </Stack>

              <Slider
                value={
                  strategySensitivity
                }
                min={0}
                max={100}
                step={1}
                disabled={
                  executionMutation
                    .isPending
                }
                onChange={(
                  _,
                  value,
                ) => {
                  const nextValue =
                    Array.isArray(
                      value,
                    )
                      ? value[0]
                      : value;

                  setStrategyProfile(
                    'manual',
                  );

                  setStrategySensitivity(
                    nextValue,
                  );

                  resetExecution();
                }}
                marks={[
                  {
                    value: 0,
                    label: 'Inercia',
                  },
                  {
                    value: 50,
                    label: 'Equilibrio',
                  },
                  {
                    value: 100,
                    label: 'IA',
                  },
                ]}
                sx={{
                  mt: 0.5,
                  color: '#38bdf8',

                  '& .MuiSlider-markLabel':
                    {
                      color:
                        '#64748b',

                      fontSize: 11,
                    },
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.6,
                  color: '#64748b',
                  lineHeight: 1.5,
                }}
              >
                0 % prioriza la base inercial y
                100 % da el máximo peso permitido a
                la señal IA. Este porcentaje no es el
                riesgo estimado de la recomendación.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            px: {
              xs: 1.75,
              md: 2,
            },

            py: 1.4,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 800,
                }}
              >
                {contextLabel}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.2,
                  color: '#64748b',
                }}
              >
                {contextDetail}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              {missingCost && (
                <TextField
                  size="small"
                  type="number"
                  label="Coste producto"
                  value={
                    manualCostInput
                  }
                  disabled={
                    executionMutation
                      .isPending
                  }
                  onChange={(
                    event,
                  ) => {
                    setManualCostInput(
                      event.target.value,
                    );

                    resetExecution();
                  }}
                  sx={{
                    width: 160,
                  }}
                />
              )}

              <TextField
                size="small"
                type="number"
                label="Cantidad"
                value={
                  quantityInput
                }
                disabled={
                  executionMutation
                    .isPending
                }
                onChange={(
                  event,
                ) => {
                  setQuantityInput(
                    event.target.value,
                  );

                  resetExecution();
                }}
                sx={{
                  width: 150,
                }}
              />

              <Chip
                size="small"
                label={
                  executionMutation
                    .isPending
                    ? 'Ejecutando'
                    : result
                      ? 'Resultado disponible'
                      : 'Pendiente de ejecución'
                }
                variant="outlined"
                sx={{
                  color: '#94a3b8',

                  borderColor:
                    'rgba(148,163,184,0.24)',
                }}
              />

              <Button
                size="small"
                variant="contained"
                disabled={
                  !ready
                  || executionMutation
                    .isPending
                }
                onClick={() =>
                  executionMutation
                    .mutate()
                }
                sx={{
                  textTransform:
                    'none',

                  fontWeight: 850,
                  boxShadow: 'none',
                }}
              >
                {executionMutation
                  .isPending
                  ? 'Ejecutando...'
                  : result
                    ? 'Ejecutar de nuevo'
                    : 'Ejecutar optimización'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {preflightQuery.isError && (
        <Alert
          severity="error"
        >
          No se pudo comprobar la disponibilidad de
          optimización para este contexto.
        </Alert>
      )}

      {preflightQuery.data
        ?.status
        === 'unavailable' && (
        <Alert
          severity="warning"
        >
          Este contexto no está disponible para ejecución.
          {' '}
          {
            preflightQuery
              .data
              .reason_codes
              .join(' · ')
          }
        </Alert>
      )}

      {executionMutation.isPending && (
        <Paper
          elevation={0}
          sx={{
            p: 1.75,
            borderRadius: 2.5,

            border:
              '1px solid rgba(56,189,248,0.16)',

            bgcolor:
              'rgba(15,23,42,0.58)',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#cbd5e1',
              fontWeight: 750,
            }}
          >
            Ejecutando optimización
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.3,
              mb: 1.2,
              color: '#64748b',
            }}
          >
            El cálculo utiliza el motor de optimización
            existente. No modifica ningún precio.
          </Typography>

          <LinearProgress />
        </Paper>
      )}

      {executionMutation.isError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                executionMutation
                  .mutate()
              }
            >
              Reintentar
            </Button>
          }
        >
          No se pudo ejecutar la optimización.
          {' '}
          {errorMessage}
        </Alert>
      )}

      {result?.status
        === 'no_recommendation' && (
        <Alert
          severity="info"
        >
          El optimizador terminó la evaluación, pero no
          produjo una recomendación aplicable para este
          contexto.
        </Alert>
      )}

      {result?.status === 'ok' && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,

            border:
              '1px solid rgba(129,140,248,0.18)',

            background:
              'linear-gradient(135deg, rgba(99,102,241,0.075) 0%, rgba(15,23,42,0.72) 42%, rgba(14,165,233,0.045) 100%)',

            boxShadow:
              '0 16px 42px rgba(2,6,23,0.20)',

            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.75,
                md: 2,
              },

              py: 1.5,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#a5b4fc',
                    fontWeight: 850,
                    letterSpacing: 0.8,
                  }}
                >
                  Resultado del optimizador
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Recomendación para esta evaluación
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                flexWrap="wrap"
              >
                <Chip
                  size="small"
                  label={
                    `${
                      getStrategyLabel(
                        result
                          .request
                          .strategy_profile,
                      )
                    } · ${
                      numberFormatter.format(
                        result
                          .request
                          .strategy_sensitivity,
                      )
                    } %`
                  }
                  variant="outlined"
                  sx={{
                    color: '#bae6fd',

                    borderColor:
                      'rgba(56,189,248,0.28)',
                  }}
                />

                <Chip
                  size="small"
                  label={
                    getReviewModeLabel(
                      result
                        .risks
                        .review_mode,
                    )
                  }
                  variant="outlined"
                  sx={{
                    color: '#cbd5e1',

                    borderColor:
                      'rgba(165,180,252,0.30)',
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          <Divider />

          <Box
            sx={{
              p: {
                xs: 1.25,
                md: 1.5,
              },
            }}
          >
            <Box
              sx={{
                display: 'grid',

                gridTemplateColumns: {
                  xs: '1fr',

                  lg:
                    'minmax(0, 0.9fr) minmax(0, 1.35fr) minmax(0, 0.9fr)',
                },

                gap: 1,
                alignItems: 'stretch',
              }}
            >
              <DecisionScenarioCard
                label="Base / Inercial"
                price={
                  result
                    .scenarios
                    .inertial_price
                }
                marginPct={
                  result
                    .scenarios
                    .inertial_margin_pct
                }
                markupPct={
                  result
                    .scenarios
                    .inertial_markup_pct
                }
                cost={
                  result
                    .recommendation
                    .cost_used
                }
                referencePrice={
                  referencePrice
                }
                currencyCode={
                  currencyCode
                }
              />

              <RecommendationDecisionCard
                price={
                  result
                    .recommendation
                    .suggested_price
                }
                marginPct={
                  result
                    .recommendation
                    .suggested_margin_pct
                }
                markupPct={
                  result
                    .recommendation
                    .suggested_markup_pct
                }
                cost={
                  result
                    .recommendation
                    .cost_used
                }
                referencePrice={
                  referencePrice
                }
                haircutVsMaxAiPct={
                  result
                    .scenarios
                    .haircut_vs_max_ai_pct
                }
                currencyCode={
                  currencyCode
                }
              />

              <DecisionScenarioCard
                label="Máximo IA"
                price={
                  result
                    .scenarios
                    .max_ai_price
                }
                marginPct={
                  result
                    .scenarios
                    .max_ai_margin_pct
                }
                markupPct={
                  result
                    .scenarios
                    .max_ai_markup_pct
                }
                cost={
                  result
                    .recommendation
                    .cost_used
                }
                referencePrice={
                  referencePrice
                }
                currencyCode={
                  currencyCode
                }
              />
            </Box>

            {result
              .scenarios
              .pricing_matrix
              && result
                .scenarios
                .pricing_matrix
                .length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 1.25,
                    borderRadius: 2,

                    border:
                      '1px solid rgba(148,163,184,0.14)',

                    bgcolor:
                      'rgba(15,23,42,0.30)',

                    overflow:
                      'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.1,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: '#94a3b8',
                        fontWeight: 850,
                        letterSpacing: 0.7,
                      }}
                    >
                      Matriz de negociación · volumen
                    </Typography>
                  </Box>

                  <Divider />

                  <TableContainer>
                    <Table
                      size="small"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            Cantidad
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            Precio objetivo
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            Descuento
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            Margen unitario
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {result
                          .scenarios
                          .pricing_matrix
                          .map(
                            (tier) => (
                              <TableRow
                                key={
                                  `${
                                    tier.quantity
                                  }-${
                                    tier.suggested_price
                                  }`
                                }
                              >
                                <TableCell>
                                  {formatNumber(
                                    tier.quantity,
                                  )}
                                </TableCell>

                                <TableCell
                                  align="right"
                                >
                                  {formatCurrency(
                                    tier.suggested_price,
                                    currencyCode,
                                  )}
                                </TableCell>

                                <TableCell
                                  align="right"
                                >
                                  {formatPct(
                                    tier
                                      .suggested_discount
                                    * 100,
                                  )}
                                </TableCell>

                                <TableCell
                                  align="right"
                                >
                                  {formatCurrency(
                                    tier.unit_margin,
                                    currencyCode,
                                  )}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
          </Box>

          <Divider />

          <Box
            sx={{
              px: {
                xs: 1.5,
                md: 1.75,
              },

              py: 1.5,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#94a3b8',
                fontWeight: 850,
                letterSpacing: 0.7,
              }}
            >
              Calidad de la recomendación
            </Typography>

            <Box
              sx={{
                mt: 1,

                display: 'grid',

                gridTemplateColumns: {
                  xs: '1fr',

                  md:
                    'repeat(2, minmax(0, 1fr))',
                },

                gap: 1,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.4,
                  borderRadius: 2,

                  border:
                    '1px solid rgba(148,163,184,0.14)',

                  bgcolor:
                    'rgba(15,23,42,0.30)',
                }}
              >
                <DecisionConfidenceScale
                  value={
                    result
                      .recommendation
                      .confidence_label
                  }
                />
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.4,
                  borderRadius: 2,

                  border:
                    '1px solid rgba(148,163,184,0.14)',

                  bgcolor:
                    'rgba(15,23,42,0.30)',
                }}
              >
                <DecisionRiskScale
                  score={
                    result
                      .risks
                      .score
                  }
                  label={
                    result
                      .risks
                      .label
                  }
                />
              </Paper>
            </Box>
          </Box>

          <Divider />

          <Box
            sx={{
              px: {
                xs: 1.75,
                md: 2,
              },

              py: 1.35,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#64748b',
                fontWeight: 750,
              }}
            >
              PROCEDENCIA DEL MODELO Y LOS DATOS
            </Typography>

            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              spacing={{
                xs: 0.4,
                md: 2.5,
              }}
              useFlexGap
              flexWrap="wrap"
              sx={{
                mt: 0.55,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                }}
              >
                Familia:{' '}
                {
                  result
                    .provenance
                    ?.model_family
                  ?? '—'
                }
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                }}
              >
                Datos hasta:{' '}
                {formatDate(
                  result
                    .provenance
                    ?.data_cutoff_at,
                )}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                }}
              >
                Entrenamiento:{' '}
                {formatDate(
                  result
                    .provenance
                    ?.training_finished_at,
                )}
              </Typography>
            </Stack>
          </Box>
        </Paper>
      )}

      {result
        && contextEvidence
        && (
          <PricingDecisionContextEvidencePanel
            evidence={
              contextEvidence
            }
          />
        )}

      {result
        && contextSupplement}

      {result
        && result.risks.review_mode
          !== 'evaluation_possible'
        && (
          <Alert
            severity={
              result
                .risks
                .review_mode
                === 'reference_only'
                ? 'warning'
                : 'info'
            }
          >
            {result
              .risks
              .review_mode
              === 'reference_only'
              ? (
                'El resultado debe utilizarse únicamente '
                + 'como referencia comercial.'
              )
              : (
                'La recomendación requiere revisión '
                + 'comercial antes de cualquier decisión.'
              )}
          </Alert>
        )}

      {result?.risks.warnings.map(
        (
          warning,
          index,
        ) => (
          <Alert
            key={
              `${warning}-${index}`
            }
            severity="warning"
          >
            {warning}
          </Alert>
        ),
      )}
    </Stack>
  );
}